import React from 'react';
import { Task } from '../../types';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

interface DueDiligenceTimelineProps {
  tasks: Task[];
  categories: Array<{ id: string; name: string; color: string }>;
}

export default function DueDiligenceTimeline({ tasks, categories }: DueDiligenceTimelineProps) {
  const sortedTasks = [...tasks].sort((a, b) => 
    new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  );

  const getStatusIcon = (status: string, dueDate: string) => {
    if (status === 'completed') {
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }
    if (new Date(dueDate) < new Date()) {
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
    return <Clock className="w-5 h-5 text-yellow-500" />;
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.color || 'gray';
  };

  return (
    <div className="p-6">
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
        
        <div className="space-y-8">
          {sortedTasks.map((task, index) => (
            <div key={task.id} className="relative">
              <div className="flex items-start">
                <div className="absolute left-0 mt-1">
                  {getStatusIcon(task.status, task.dueDate)}
                </div>
                
                <div className="ml-12">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {task.title}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${getCategoryColor(task.category!)}-100 text-${getCategoryColor(task.category!)}-800`}>
                      {categories.find(c => c.id === task.category)?.name}
                    </span>
                  </div>
                  
                  <div className="mt-1 text-sm text-gray-500">
                    Due: {new Date(task.dueDate).toLocaleDateString()}
                  </div>
                  
                  {task.description && (
                    <p className="mt-2 text-sm text-gray-600">
                      {task.description}
                    </p>
                  )}
                  
                  <div className="mt-3 flex items-center space-x-4">
                    {task.assignee && (
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {task.assignee[0].toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600">{task.assignee}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <div className="w-24 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: `${task.progress || 0}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">
                        {task.progress || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}